/* ProGate — wrap any UI element to lock it behind Pro.
 *
 * Usage:
 *   <ProGate theme={theme} feature="Hit-Rate verification">
 *     <HitRateBadge ... />
 *   </ProGate>
 *
 * Free users see a blurred preview of the children with a small lock chip
 * and a tap-to-upgrade gesture that opens the PaywallSheet.
 */
import { useState } from "react";
import { Lock, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import SignInSheet from "@/components/v2/SignInSheet";
import PaywallSheet from "@/components/v2/PaywallSheet";

export default function ProGate({ children, theme, feature, sub, compact }) {
  const { user, isPro } = useAuth();
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openPaywall, setOpenPaywall] = useState(false);

  if (isPro) return children;

  const handleTap = () => {
    if (!user) setOpenSignIn(true);
    else setOpenPaywall(true);
  };

  const dark = theme?.id === "night" || theme?.id === "storm";

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleTap}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTap();
          }
        }}
        data-testid={`pro-gate-${(feature || "feature").toLowerCase().replace(/\s+/g, "-")}`}
        className="relative w-full text-left rounded-2xl overflow-hidden transition-transform active:scale-[0.99] cursor-pointer"
        style={{
          border: `1px dashed ${dark ? "rgba(245,158,11,0.45)" : "rgba(245,158,11,0.55)"}`,
          background: dark ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.08)",
        }}
      >
        {/* Blurred preview of children behind the lock surface */}
        <div
          aria-hidden
          className="select-none pointer-events-none"
          style={{
            filter: "blur(6px) saturate(0.85)",
            opacity: 0.55,
            maxHeight: compact ? 90 : 220,
            overflow: "hidden",
          }}
        >
          {children}
        </div>
        {/* Lock overlay */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-5"
          style={{
            background: dark
              ? "linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.85) 100%)"
              : "linear-gradient(180deg, rgba(255,251,235,0.55) 0%, rgba(255,251,235,0.92) 100%)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
        >
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-1.5"
            style={{
              background: "rgba(245,158,11,0.18)",
              border: "1px solid rgba(245,158,11,0.45)",
              color: "#b45309",
            }}
          >
            <Lock size={10} weight="bold" />
            <span className="text-[9px] font-bold uppercase tracking-[0.18em]">Pro</span>
          </span>
          <div
            className="font-semibold text-[13px] leading-tight"
            style={{ color: theme?.textPrimary || "#0f172a" }}
          >
            {feature || "Unlock with Pro"}
          </div>
          {sub && (
            <div
              className="mt-1 text-[11px] leading-snug max-w-[28ch]"
              style={{ color: theme?.textSecondary || "#475569" }}
            >
              {sub}
            </div>
          )}
          <span
            className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ color: "#b45309" }}
          >
            <Sparkle size={10} weight="fill" />
            {user ? "Tap · ₹49 / month" : "Tap · 7-day free trial"}
          </span>
        </div>
      </div>
      <SignInSheet open={openSignIn} onClose={() => setOpenSignIn(false)} theme={theme} />
      <PaywallSheet open={openPaywall} onClose={() => setOpenPaywall(false)} theme={theme} />
    </>
  );
}
